import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Justify, Segment } from 'tea-component';
import useStickyFilter from '../Filter/hooks/useStickyFilter';
import {
  SEGMENT_FLOAT_STICKY_SYNC_EVENT,
  type ISegmentFloatStickySyncDetail,
} from '@/pages/dashboard/utils/segmentFloatStickySync';
import { useLocal} from '../../utils/customHook';
import { getLocale, shallowEqual, useSelector } from 'umi';
import { getExplainVal } from '../../utils/getExplainVal';
import "./index.scss";
import { ComponentConfigType, SegmentComponentProps } from './type';
import classNames from 'classnames';
import { DownloadButton } from '../Plugin/DownloadButton';
import { downloadExcel, convertCommonTableToDownloadConfig } from '../../utils/download/index';
import type { IDownloadConfig } from '../../utils/download/_type';
import { getName } from '@home/utils/common';
import { dynamicString } from '@home/utils/dynamicString';

const SegmentScrollCo = (props: SegmentComponentProps) => {
  const { config, children, rightSegment, className, style, data: dataProps, changeData: changeDataProps, ...restProps } = props;
  const { scrollMarginTop, isScroll = true, componentSegment = [], variant = 'default', showDownloadBtn = false, downloadFileNameEn = '', downloadFileNameCn = '', observerThreshold = 1, observerRootMargin = '0px', segmentFloatStickySyncId, floatHeaderStickyDelayMs = 0 } = config || {};
  const [showSegment, setShowSegment] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(componentSegment[0]?.value);
  const [isManuallyChanged, setIsManuallyChanged] = useState(false);
  const { isEN } = useLocal();
  const [registeredTables, setRegisteredTables] = useState<any[]>([]);

  const changeData = useCallback((name: string, value: any) => {
    if (name === '__registerTable__') {
      setRegisteredTables((prev) => {
        const filtered = prev.filter((t) => t.id !== value.id);
        return [...filtered, value];
      });
    } else if (name === '__unregisterTable__') {
      setRegisteredTables((prev) => prev.filter((t) => t.id !== value));
    } else {
      changeDataProps?.(name, value);
    }
  }, [changeDataProps]);

  const registeredTablesHasValidData = useMemo(() => {
    if (!registeredTables || registeredTables.length === 0) {
      return false;
    }

    // 检查是否至少有一个表格有数据
    return registeredTables.some((table: any) => {
      const dataSource = table.dataSource || [];
      return Array.isArray(dataSource) && dataSource.length > 0;
    });
  }, [registeredTables]);

  // 获取顶部筛选器高度
  const { scrollTop } = useStickyFilter(true);
  // 获取Segment的高度
  const segmentTop: number = window.document.getElementById('Segment')?.offsetTop ?? 0;
  const { explainInfo } = useSelector((state) =>
    ({
      explainInfo: state.dashboard?.explainInfo,
    }), shallowEqual);

  const shouldStickFloatHeader = scrollTop >= segmentTop + 100;

  // Segment 浮动吸顶：可配置延迟；未配置或 delay≤0 时行为与原先一致
  useEffect(() => {
    const delayMs =
      typeof floatHeaderStickyDelayMs === 'number' && floatHeaderStickyDelayMs > 0
        ? floatHeaderStickyDelayMs
        : 0;

    if (delayMs <= 0) {
      setShowSegment(shouldStickFloatHeader);
      return;
    }

    if (!shouldStickFloatHeader) {
      setShowSegment(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowSegment(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [shouldStickFloatHeader, floatHeaderStickyDelayMs]);

  // 与筛选器 segmentFloatStickySyncId 配对时，派发浮动吸顶状态
  useEffect(() => {
    if (!segmentFloatStickySyncId) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent<ISegmentFloatStickySyncDetail>(
        SEGMENT_FLOAT_STICKY_SYNC_EVENT,
        {
          detail: { syncId: segmentFloatStickySyncId, stuck: showSegment },
        },
      ),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent<ISegmentFloatStickySyncDetail>(
          SEGMENT_FLOAT_STICKY_SYNC_EVENT,
          {
            detail: { syncId: segmentFloatStickySyncId, stuck: false },
          },
        ),
      );
    };
  }, [showSegment, segmentFloatStickySyncId]);

  // 生成Segment选项
  const options = useMemo(() => componentSegment?.map((item: ComponentConfigType) => {
    const { enName, cnName, enTooltip, cnTooltip, rainbowKey, value, disable, explainEn = '', explainCn = '' } = item;
    const explain = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
    return {
      text: getName(isEN, dynamicString(enName, dataProps), dynamicString(cnName, dataProps)),
      value,
      bubble: explain?.replaceAll('\\n', '<br/>'),
      tooltip: getName(isEN, dynamicString(enTooltip, dataProps), dynamicString(cnTooltip, dataProps)),
      disabled: disable !== undefined ? disable : false, // 禁用兜底
    };
  }), [componentSegment, explainInfo, isEN, dataProps]);

  // 渲染左侧Segment
  const renderSegment = () => (
    <Segment
      options={options}
      value={selectedSegment}
      onChange={(value) => handleSegmentChange(value)}
    />
  );

  const handleDownloadAllTables = async () => {
    if (!registeredTables || registeredTables.length === 0) {
      return;
    }

    try {
      // 根据 componentSegment 的顺序对 registeredTables 进行排序
      const sortedTables = componentSegment
        .map(segment => registeredTables.find(table => table.titleEn === segment.enName || table.titleCn === segment.cnName))
        .filter(Boolean);

      const downloadConfig: IDownloadConfig = {
        fileName: (isEN ? downloadFileNameEn : downloadFileNameCn) || '',
        tabs: sortedTables.map((table: any) => {
          const columns = table.columns || [];
          const dataSource = table.dataSource || [];
          const tabName = getName(isEN, table.titleEn, table.titleCn) || table.id || 'Sheet';

          if (table.headerRows && table.mergeCells) {
            return {
              tabName,
              dataSource,
              columns,
              headerRows: table.headerRows,
              mergeCells: table.mergeCells,
            };
          }

          return convertCommonTableToDownloadConfig(
            dataSource,
            columns,
            tabName,
            isEN,
          );
        }),
      };

      await downloadExcel(downloadConfig);
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  // 渲染右侧下拉组件,可参考实时用户国家标签页
  const renderRightSegment = () => (
    <div className='dbd-segment-scroll-co-right-segment'>
      {rightSegment && rightSegment}
      {showDownloadBtn && registeredTablesHasValidData && (
        <DownloadButton
          className='dbd-segment-scroll-co-right-segment-btn'
          onClick={() => {
            handleDownloadAllTables();
          }}
        />
      )}
    </div>
  );

  // 处理Segment选项变化
  const handleSegmentChange = (value: string) => {
    setSelectedSegment(value);
    setIsManuallyChanged(true);
    if (isScroll) {
      scrollToCard(value);
    }
  };

  // 滚动到对应卡片
  const scrollToCard = (segmentValue: string) => {

    const cardElement = window.document.getElementById(segmentValue);

    if (cardElement) {
      const observerOptions = {
        root: null,
        rootMargin: "0px",
        threshold: 1
      };

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 当卡片进入视口，取消手动更改标记
            setIsManuallyChanged(false);
            // 停止观察
            observer.disconnect();
          }
        });
      }, observerOptions);

      observer.observe(cardElement);

      cardElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const numCards = componentSegment.length;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const refArray = Array.from({ length: numCards }, () => useRef(null));


  // 记录当前视口中可见的卡片 id 集合
  const visibleIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isManuallyChanged) {
      const segmentValues = componentSegment.map((s: ComponentConfigType) => s.value);

      const callback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          const id = entry.target.getAttribute('id');
          if (!id) return;
          if (entry.isIntersecting) {
            visibleIdsRef.current.add(id);
          } else {
            visibleIdsRef.current.delete(id);
          }
        });

        // 在所有可见卡片中，选择 componentSegment 顺序最靠前的
        for (const value of segmentValues) {
          if (visibleIdsRef.current.has(value)) {
            setSelectedSegment(value);
            return;
          }
        }
      };

      const options = {
        root: null,
        rootMargin: observerRootMargin,
        threshold: observerThreshold,
      };

      // 观察元素可见性
      const observer = new IntersectionObserver(callback, options);
      refArray.forEach(ref => {
        if (ref.current instanceof Element) {
          observer.observe(ref.current);
        }
      });

      // 清理观察者
      return () => {
        visibleIdsRef.current.clear();
        refArray.forEach(ref => {
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        });
      };
    }
  }, [isManuallyChanged, refArray, componentSegment, observerRootMargin, observerThreshold]);

  return (
    <Card className={classNames('dbd-segment-scroll-co margin-t-20', className, {
      'dbd-segment-scroll-co-floating': variant === 'floating',
    })} style={style}>
      <Card.Body>
        {/* 吸顶Segment */}
        {
          showSegment && (
            <div className={`header-segment visible`}>
              <Justify
                left={renderSegment()}
                right={renderRightSegment()}
              />
            </div>
          )
        }

        {/* Segment */}
        <div id='Segment' className='background-bg'>
          <Justify
            left={renderSegment()}
            right={renderRightSegment()}
          />
        </div>

        <div id="scroll-box">
          {
            React.Children.map(children, (child: any,index: number) => React.cloneElement(child, {
              ...restProps,
              id: child.id,
              style: { scrollMarginTop },
              refArray,
              index,
              changeData
            }))
          }
        </div>
      </Card.Body>
    </Card>
  );
};

export default SegmentScrollCo;
